import apiService from './apiService';

class CompanyDataService {
  constructor() {
    this.data = [];
    this.filteredData = [];
    this.companyId = '1728965891007x215886838679286700';
    this.lastUpdate = null;
  }

  // Carrega dados do arquivo JSON específico da empresa
  async loadData() {
    try {
      const response = await fetch('/1728965891007x215886838679286700.json');
      const rawData = await response.json();
      
      // Filtra apenas dados da empresa específica e remove entradas inválidas
      this.data = rawData.filter(item => 
        item.empresa === this.companyId && 
        item.codbar && 
        item.description &&
        item.num_consultas > 0
      );
      this.filteredData = this.data;
      this.lastUpdate = new Date().toISOString();
      
      return this.data;
    } catch (error) {
      console.error('Erro ao carregar dados da empresa:', error);
      throw error;
    }
  }

  // Atualiza dados da API do Supabase
  async updateFromApi() {
    try {
      console.log('🔄 Atualizando dados da API...');
      const result = await apiService.updateLocalJsonFile();
      
      if (result.success) {
        // Atualizar dados diretamente com os dados da API
        this.data = result.data.filter(item => 
          item.empresa === this.companyId && 
          item.codbar && 
          item.description &&
          item.num_consultas > 0
        );
        this.filteredData = this.data;
        this.lastUpdate = result.timestamp;
        
        console.log(`✅ Dados atualizados com sucesso: ${this.data.length} produtos`);
        return result;
      } else {
        throw new Error('Falha ao atualizar dados');
      }
    } catch (error) {
      console.error('❌ Erro ao atualizar dados da API:', error);
      throw error;
    }
  }

  // Verifica se há atualizações disponíveis
  async checkForUpdates() {
    try {
      const updateInfo = await apiService.checkForUpdates();
      return updateInfo;
    } catch (error) {
      console.error('Erro ao verificar atualizações:', error);
      throw error;
    }
  }

  // Obtém informações da última atualização
  getLastUpdateInfo() {
    return {
      lastUpdate: this.lastUpdate,
      totalProducts: this.data.length,
      companyId: this.companyId
    };
  }

  // Formatar valores
  formatNumber(value) {
    return new Intl.NumberFormat('pt-BR').format(value);
  }

  // Trata valores nulos ou undefined
  safeValue(value, defaultValue = '-') {
    return value && value !== null && value !== undefined ? value : defaultValue;
  }

  // Obtém estatísticas gerais da empresa
  getStats() {
    const stats = {
      totalProdutos: this.filteredData.length,
      totalConsultas: this.filteredData.reduce((sum, item) => sum + (item.num_consultas || 0), 0),
      totalDispositivos: new Set(this.filteredData.map(item => item.dispositivo)).size,
      totalFiliais: new Set(this.filteredData.map(item => item.filial)).size,
      mediaConsultasPorProduto: 0
    };

    stats.mediaConsultasPorProduto = stats.totalProdutos > 0 ? 
      (stats.totalConsultas / stats.totalProdutos) : 0;

    return stats;
  }

  // Produtos mais consultados
  getProdutosMaisConsultados(limit = 10) {
    return this.filteredData
      .sort((a, b) => (b.num_consultas || 0) - (a.num_consultas || 0))
      .slice(0, limit)
      .map(item => ({
        codbar: item.codbar,
        description: this.safeValue(item.description),
        apelido_dispositivo: this.safeValue(item.apelido_dispositivo),
        num_consultas: item.num_consultas || 0,
        filial: this.safeValue(item.filial)
      }));
  }

  // Estatísticas por filial
  getEstatisticasPorFilial() {
    const filialStats = {};
    
    this.filteredData.forEach(item => {
      const filial = this.safeValue(item.filial, 'Não informada');
      if (!filialStats[filial]) {
        filialStats[filial] = {
          produtos: 0,
          consultas: 0,
          dispositivos: new Set()
        };
      }
      
      filialStats[filial].produtos += 1;
      filialStats[filial].consultas += (item.num_consultas || 0);
      if (item.dispositivo) {
        filialStats[filial].dispositivos.add(item.dispositivo);
      }
    });

    return Object.entries(filialStats).map(([filial, stats]) => ({
      filial,
      produtos: stats.produtos,
      consultas: stats.consultas,
      dispositivos: stats.dispositivos.size,
      mediaConsultas: stats.produtos > 0 ? (stats.consultas / stats.produtos).toFixed(1) : 0
    })).sort((a, b) => b.consultas - a.consultas);
  }

  // Estatísticas por dispositivo
  getEstatisticasPorDispositivo() {
    const dispositivoStats = {};
    
    this.filteredData.forEach(item => {
      const dispositivo = this.safeValue(item.apelido_dispositivo, 'Não informado');
      if (!dispositivoStats[dispositivo]) {
        dispositivoStats[dispositivo] = {
          produtos: 0,
          consultas: 0,
          filiais: new Set()
        };
      }
      
      dispositivoStats[dispositivo].produtos += 1;
      dispositivoStats[dispositivo].consultas += (item.num_consultas || 0);
      if (item.filial) {
        dispositivoStats[dispositivo].filiais.add(item.filial);
      }
    });

    return Object.entries(dispositivoStats).map(([dispositivo, stats]) => ({
      dispositivo,
      produtos: stats.produtos,
      consultas: stats.consultas,
      filiais: stats.filiais.size,
      mediaConsultas: stats.produtos > 0 ? (stats.consultas / stats.produtos).toFixed(1) : 0
    })).sort((a, b) => b.consultas - a.consultas);
  }

  // Dados para gráfico de consultas por filial
  getConsultasPorFilial() {
    const filialConsultas = {};
    
    this.filteredData.forEach(item => {
      const filial = `Filial ${this.safeValue(item.filial, 'N/A')}`;
      filialConsultas[filial] = (filialConsultas[filial] || 0) + (item.num_consultas || 0);
    });

    return Object.entries(filialConsultas)
      .map(([filial, consultas]) => ({
        label: filial,
        value: consultas
      }))
      .sort((a, b) => b.value - a.value);
  }

  // Dados para gráfico de produtos por categoria (baseado na descrição)
  getProdutosPorCategoria() {
    const categorias = {};
    
    this.filteredData.forEach(item => {
      const descricao = item.description || '';
      let categoria = 'Outros';
      
      // Categorização simples baseada em palavras-chave
      if (descricao.includes('CARNE') || descricao.includes('BACON') || descricao.includes('MORTADELA') || descricao.includes('PRESUNTO')) {
        categoria = 'Carnes e Frios';
      } else if (descricao.includes('REFRIG') || descricao.includes('SUCO') || descricao.includes('AGUA')) {
        categoria = 'Bebidas';
      } else if (descricao.includes('PAO') || descricao.includes('BISC') || descricao.includes('BOMBOM')) {
        categoria = 'Panificação e Doces';
      } else if (descricao.includes('DETERG') || descricao.includes('PAPEL') || descricao.includes('LIMPADOR')) {
        categoria = 'Limpeza e Higiene';
      } else if (descricao.includes('LEITE') || descricao.includes('QUEIJO') || descricao.includes('IOGURTE')) {
        categoria = 'Laticínios';
      } else if (descricao.includes('FRALDA') || descricao.includes('LENCO')) {
        categoria = 'Bebê e Infantil';
      }
      
      categorias[categoria] = (categorias[categoria] || 0) + 1;
    });

    return Object.entries(categorias).map(([categoria, count]) => ({
      label: categoria,
      value: count
    })).sort((a, b) => b.value - a.value);
  }

  // Timeline de consultas (simulado - dados não têm timestamp específico)
  getTimelineConsultas() {
    // Como não temos dados temporais específicos, vamos simular baseado na distribuição
    const meses = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun'];
    const totalConsultas = this.filteredData.reduce((sum, item) => sum + (item.num_consultas || 0), 0);
    
    return meses.map((mes, index) => ({
      month: mes,
      label: mes + '/2024',
      value: Math.floor(totalConsultas / 6) + Math.floor(Math.random() * (totalConsultas * 0.1))
    }));
  }

  // Filtrar dados
  filterData(filters) {
    let filtered = this.data;

    // Filtro por filial
    if (filters.filial && filters.filial !== '') {
      filtered = filtered.filter(item => 
        item.filial && item.filial.toString() === filters.filial
      );
    }

    // Filtro por dispositivo
    if (filters.dispositivo && filters.dispositivo !== '') {
      filtered = filtered.filter(item => 
        item.apelido_dispositivo && item.apelido_dispositivo.includes(filters.dispositivo)
      );
    }

    // Filtro por número mínimo de consultas
    if (filters.minConsultas && filters.minConsultas > 0) {
      filtered = filtered.filter(item => 
        (item.num_consultas || 0) >= filters.minConsultas
      );
    }

    // Busca por produto
    if (filters.busca && filters.busca.trim() !== '') {
      const searchTerm = filters.busca.toLowerCase();
      filtered = filtered.filter(item => {
        const description = (item.description || '').toLowerCase();
        const codbar = (item.codbar || '').toString().toLowerCase();
        
        return description.includes(searchTerm) || codbar.includes(searchTerm);
      });
    }

    this.filteredData = filtered;
    return filtered;
  }

  // Obtém dados filtrados atuais
  getFilteredData() {
    return this.filteredData;
  }

  // Obtém todos os dados
  getAllData() {
    return this.data;
  }

  // Obter valores únicos para filtros
  getUniqueFiliais() {
    const filiais = new Set();
    this.data.forEach(item => {
      if (item.filial) {
        filiais.add(item.filial);
      }
    });
    return Array.from(filiais).sort((a, b) => a.localeCompare(b));
  }

  getUniqueDispositivos() {
    const dispositivos = new Set();
    this.data.forEach(item => {
      if (item.apelido_dispositivo) {
        dispositivos.add(item.apelido_dispositivo);
      }
    });
    return Array.from(dispositivos).sort();
  }

  // Reset dos filtros
  resetFilters() {
    this.filteredData = this.data;
    return this.filteredData;
  }
}

// Exportar instância singleton
const companyDataServiceInstance = new CompanyDataService();
export default companyDataServiceInstance;
